import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CaretDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  WarningCircleIcon,
  TrendUpIcon,
} from '@phosphor-icons/react';
import type { RuleAudit } from 'src/api/rebates/types/auditTypes';
import { formatCurrency } from '@erp/shared-utils';
import { TierProgressBar } from 'src/components/rebates/audit/TierProgressBar';

interface RuleGroupedViewProps {
  rules: RuleAudit[];
}

export function RuleGroupedView({ rules }: RuleGroupedViewProps) {
  // Start with all rules expanded
  const [expandedRules, setExpandedRules] = useState<Set<string>>(
    new Set(rules.map((r) => r.ruleId)),
  );

  const toggleRule = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  const getStatusInfo = (status: RuleAudit['status']) => {
    switch (status) {
      case 'tier_met':
        return {
          icon: CheckCircleIcon,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: 'Tier Met',
        };
      case 'qualified_below_threshold':
        return {
          icon: WarningCircleIcon,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          label: 'Below Threshold',
        };
      case 'no_qualifying_volume':
        return {
          icon: XCircleIcon,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'No Volume',
        };
      case 'manual_amounts_only':
        return {
          icon: CheckCircleIcon,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          label: 'Manual',
        };
    }
  };

  if (rules.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No rules found for this agreement</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {rules.map((rule) => {
        const statusInfo = getStatusInfo(rule.status);
        const StatusIcon = statusInfo.icon;
        const isExpanded = expandedRules.has(rule.ruleId);

        return (
          <Card key={rule.ruleId} className="overflow-hidden">
            <Collapsible
              open={isExpanded}
              onOpenChange={() => toggleRule(rule.ruleId)}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-lg">
                          {rule.ruleName}
                        </CardTitle>
                        <Badge variant="outline" className={statusInfo.bgColor}>
                          <StatusIcon
                            className={`h-3 w-3 mr-1 ${statusInfo.color}`}
                          />
                          <span className={statusInfo.color}>
                            {statusInfo.label}
                          </span>
                        </Badge>
                        {rule.ruleType === 'manual' && (
                          <Badge variant="secondary">Manual</Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Rebate Amount</p>
                          <p className="font-semibold text-lg">
                            {formatCurrency(rule.rebateAmount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Qualifying Volume
                          </p>
                          <p className="font-medium">
                            {formatCurrency(rule.qualifyingVolume)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Affected Volume
                          </p>
                          <p className="font-medium">
                            {formatCurrency(rule.affectedVolume)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Invoices</p>
                          <p className="font-medium">{rule.invoiceCount}</p>
                        </div>
                      </div>
                    </div>

                    <CaretDownIcon
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent className="space-y-6 pt-0">
                  {/* Tier Progress */}
                  {rule.tierEvaluation && rule.ruleType === 'automatic' && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <TrendUpIcon className="h-4 w-4" />
                        Tier Progress
                      </h4>
                      <TierProgressBar
                        tierEvaluation={rule.tierEvaluation}
                        qualifyingVolume={rule.qualifyingVolume}
                        nextTier={rule.nextTier}
                      />
                      <p className="text-sm text-muted-foreground">
                        {rule.tierEvaluation.explanation}
                      </p>
                    </div>
                  )}

                  {/* Next Tier Info */}
                  {rule.nextTier && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-3">
                        <TrendUpIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-medium text-sm text-blue-900 mb-2">
                            Next Tier Available
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-blue-600">Gap to Next Tier</p>
                              <p className="font-semibold text-blue-900">
                                {formatCurrency(rule.nextTier.gapAmount)}
                              </p>
                            </div>
                            <div>
                              <p className="text-blue-600">Progress</p>
                              <p className="font-semibold text-blue-900">
                                {rule.nextTier.percentageComplete.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Division Breakdown */}
                  {rule.divisionBreakdowns.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">
                        Division Breakdown
                      </h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 font-medium">
                                Division
                              </th>
                              <th className="text-right p-3 font-medium">
                                Qualifying
                              </th>
                              <th className="text-right p-3 font-medium">
                                Affected
                              </th>
                              <th className="text-right p-3 font-medium">
                                % Applied
                              </th>
                              <th className="text-right p-3 font-medium">
                                Rebate Amount
                              </th>
                              <th className="text-right p-3 font-medium">
                                Proportion
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {rule.divisionBreakdowns.map((division) => {
                              // Calculate percentage: rebateAmount / affectedVolume
                              const percentage =
                                division.affectedVolume > 0
                                  ? (division.rebateAmount /
                                      division.affectedVolume) *
                                    100
                                  : 0;
                              return (
                                <tr
                                  key={division.divisionId}
                                  className="hover:bg-muted/30"
                                >
                                  <td className="p-3">
                                    {division.divisionName}
                                  </td>
                                  <td className="p-3 text-right">
                                    {formatCurrency(division.qualifyingVolume)}
                                  </td>
                                  <td className="p-3 text-right">
                                    {formatCurrency(division.affectedVolume)}
                                  </td>
                                  <td className="p-3 text-right text-muted-foreground">
                                    {percentage.toFixed(2)}%
                                  </td>
                                  <td className="p-3 text-right font-medium">
                                    {formatCurrency(division.rebateAmount)}
                                  </td>
                                  <td className="p-3 text-right text-muted-foreground">
                                    {(division.proportion * 100).toFixed(1)}%
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Contributing Invoices Summary */}
                  {rule.contributingInvoices.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">
                        Contributing Invoices (
                        {rule.contributingInvoices.length})
                      </h4>
                      <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 font-medium">
                                Invoice Date
                              </th>
                              <th className="text-left p-3 font-medium">
                                Division
                              </th>
                              <th className="text-right p-3 font-medium">
                                Total Value
                              </th>
                              <th className="text-right p-3 font-medium">
                                Qualifying
                              </th>
                              <th className="text-right p-3 font-medium">
                                Affected
                              </th>
                              <th className="text-right p-3 font-medium">
                                Rebate Earned
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {rule.contributingInvoices.map((invoice) => (
                              <tr
                                key={invoice.invoiceId}
                                className="hover:bg-muted/30"
                              >
                                <td className="p-3">
                                  {new Date(
                                    invoice.invoiceDate,
                                  ).toLocaleDateString()}
                                </td>
                                <td className="p-3">{invoice.divisionName}</td>
                                <td className="p-3 text-right">
                                  {formatCurrency(invoice.totalValue)}
                                </td>
                                <td className="p-3 text-right">
                                  {formatCurrency(invoice.qualifyingVolume)}
                                </td>
                                <td className="p-3 text-right">
                                  {formatCurrency(invoice.affectedVolume)}
                                </td>
                                <td className="p-3 text-right font-medium text-green-600">
                                  {formatCurrency(invoice.rebateEarned)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
